import { FC, useContext, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import ErrorBoundaryFallBack from "../ErrorBoundaryFallback/ErrorBoundaryFallback";
import BondAppFormContext from "../../contexts/BondAppFormContext";
import {
  Container,
  ContainerForm,
  StyledBondInfo,
  StyledBondPreview,
  StyledBondTitle,
  StyledExtraInfo,
  StyledFormHeader,
  StyledLoadingIndemnitor,
  StyledPopover,
  StyledTitle,
  SubTitle,
  Title,
  Wrapper,
  WrapperForm
} from "./FormBondApp.styled";
import { activeSchema, getUiSchemaForStep, Step } from "./FormBondApp.constants";
import { getExternalUiSchemaForStep } from "./FormBondApp.external";
import { FormStart } from "./FormStart";
import { getBondInfoData } from "./FormBondApp.helpers";
import { FormIndemnitor } from "./FormIndemnitor/FormIndemnitor";
import { StyledSubmitInfo } from "./FormIndemnitor/FormIndemnitor.styled";
import { Spinner } from "../Spinner/Spinner";
import { BondFinal } from "./steps/BondFinal/BondFinal";
import StepsIndicator from "./steps/StepsIndicator";
import BondPreview from "./steps/BondPreview/BondPreview";
import BondDelivery from "./steps/BondDelivery/BondDelivery";
import { Sidebar } from "../Sidebar/Sidebar";
import { useFormContext } from "../../FormContext";
import { Instructions } from "./steps/Instructions/Instructions";
import Button from "../Button/Button";
import { Modal } from "../Modal/Modal";
import ServiceUnavailable from "../ServiceUnavailable/ServiceUnavailable";
import { useServiceUnavailable } from "../../hooks/useServiceUnavailable";
import styled from "styled-components";

const StyledImg = styled.img`
  /* width: 100%; */

  height: 100%;
  min-height: 1024px;

  padding-left: 10px;
  padding-right: 10px;

  @media (max-width: 1024px) {
    width: 100%;
    padding: 0;
    min-height: unset;
    height: unset;
  }
`;
const StyledContainerModal = styled.div`
  overflow-y: auto;
  height: 100%;
  &::-webkit-scrollbar {
    width: 2px;
  }
  &::-webkit-scrollbar-track {
    width: 2px;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    width: 2px;
    border-radius: 4px;
    background: #576171;
  }
`;

const FormBondApp: FC = () => {
  const context = useContext(BondAppFormContext);

  const {
    step,
    setStep,
    formDataBondIndemnitor,
    setFormDataBondIndemnitor,
    isFirstLoading,
    isShippingLoading,
    isErrorSaveShipping,
    queryPaymentStatus,
    queryRecordId,
    queryAccessKey,
    bondRecordRecoveryIsLoading,
    bondRecordRecoveryIsError,
    shippingOptions,
    isError,
    issuancesRecordData,
    issuancesIsFetching,
    shippingMethods,
    paymentLink,
    product,
    isBondLoading,
    isBondError,
    isLoadingBondRecords,
    isSkipBondIndemnitor,
    appRef,
    forms,
    steps,
    submit,
    widgets,
    currentFormSchema,
    bondInfo,
    isLoadingBondIndemnitor,
    pdfUrl,
    handleDeliverySubmit,
    getLefts,
    bondRecordRecoveryError,
    currentStep,
    sidebarData,
    isBusiness,
    error,
    setAmountOfIndemnitor,
    amountOfIndemnitor,
    bondRecord,
    isShowError,
    currentBondRecordData,
    isBlocked,
    mailingState,
    fetchShippingMethods,
    fetchShippingOptions,
    isExternalProduct,
    externalProduct,
    isExternalSubmitLoading,
    externalSubmitErrorMessage
  } = useFormContext();

  const [isOpen, setIsOpen] = useState(false);
  const isServiceUnavailable = useServiceUnavailable();

  const currentUiSchema = isExternalProduct
    ? getExternalUiSchemaForStep(step, externalProduct)
    : getUiSchemaForStep(step, context.state.bondAgencyQuestionsFormUISchema);

  const bondPreviewUrl =
    externalProduct?.bond_form_preview_url || product?.bond_preview_url;

  if (isServiceUnavailable) {
    return <ServiceUnavailable />;
  }

  if (
    step !== Step.BOND_DELIVERY &&
    step !== Step.PROCESSING &&
    (bondRecordRecoveryIsLoading || isBondLoading)
  ) {
    return (
      <>
        <StyledLoadingIndemnitor>
          <Spinner />
          <span>Loading...</span>
        </StyledLoadingIndemnitor>
      </>
    );
  }

  if (
    (isFirstLoading && !product && !queryPaymentStatus) ||
    isError ||
    bondRecordRecoveryIsError ||
    isBondError ||
    isErrorSaveShipping ||
    !steps[currentStep.id - 1].label
  ) {
    return <ErrorBoundaryFallBack />;
  }

  return (
    <Container ref={appRef}>
      {isShowError && (
        <StyledPopover>
          {bondRecordRecoveryError?.response?.data?.message}
        </StyledPopover>
      )}

      {externalSubmitErrorMessage && (
        <StyledPopover>{externalSubmitErrorMessage}</StyledPopover>
      )}

      <StyledBondTitle>
        <div>{product?.name}</div>
        <StyledBondPreview>
          <StyledBondInfo>{getBondInfoData(product)}</StyledBondInfo>
          {bondPreviewUrl ? (
            <Button fullWidth={true} textColor="button-text" onClick={() => setIsOpen(true)}>
              Preview Bond Form
            </Button>
          ) : null}
        </StyledBondPreview>
      </StyledBondTitle>

      <ErrorBoundary
        fallbackRender={({ error }) => <ErrorBoundaryFallBack error={error} />}
      >
        <ContainerForm>
          <WrapperForm>
            <StyledFormHeader>
              {!product?.offline_application && (
                <StepsIndicator
                  step={currentStep.id}
                  width={currentStep.width}
                  title={steps[currentStep.id - 1].label}
                  isHideBack={
                    step === Step.PROCESSING &&
                    Boolean(queryAccessKey) &&
                    Boolean(queryRecordId)
                  }
                  isGrayed={step === Step.INSTRUCTIONS}
                  stepBack={() =>
                    currentStep?.back
                      ? setStep(currentStep.back(isBusiness))
                      : {}
                  }
                />
              )}
              <StyledTitle>
                <Title>{bondInfo?.title}</Title>
                {bondInfo?.subTitle && (
                  <SubTitle>{bondInfo?.subTitle}</SubTitle>
                )}
              </StyledTitle>

              {bondInfo.extraInfo?.map((info: string) => (
                <StyledExtraInfo>{info}</StyledExtraInfo>
              ))}
              {step === Step.BOND_OPTIONS ||
              step === Step.EXTERNAL_BOND_OPTIONS ? (
                <>
                  {mailingState && (
                    <StyledExtraInfo>
                      {" "}{mailingState} state warning:
                    </StyledExtraInfo>
                  )}
                  {bondInfo.stateDisclaimer?.map((info: string) => (
                    <StyledExtraInfo>{info}</StyledExtraInfo>
                  ))}
                </>
              ) : null}
              {step === Step.INSTRUCTIONS && product && (
                <Instructions
                  text={product?.application_instructions}
                  toTheNextStep={() => setStep(Step.START)}
                  isOfflineApplication={product?.offline_application}
                />
              )}
            </StyledFormHeader>
            {context.state?.bondApplicantFormSchema ? (
              <>
                {step &&
                  step !== Step.INSTRUCTIONS &&
                  !isBlocked &&
                  !(
                    step === Step.COMPLETE &&
                    !currentBondRecordData?.is_issued_bond_pdf_available
                  ) && (
                    <Wrapper className="bootstrap-styles-form">
                      {isLoadingBondIndemnitor && (
                        <StyledLoadingIndemnitor>
                          <Spinner />
                          <span>
                            Please wait while we check credit requirements...
                          </span>
                        </StyledLoadingIndemnitor>
                      )}

                      <div style={{ position: "relative" }}>
                        {step !== Step.BOND_INDEMNITOR &&
                          activeSchema[step] && (
                            <>
                              {forms.map(
                                ({ data, setData, active, isSkipBond }) => (
                                  <>
                                    {active && (
                                      <FormStart
                                        isLoading={
                                          step === Step.EXTERNAL_BOND_OPTIONS
                                            ? isExternalSubmitLoading
                                            : step === Step.BOND_SPECIFIC ||
                                              step === Step.BOND_OPTIONS
                                            ? isLoadingBondRecords
                                            : false
                                        }
                                        product={product}
                                        data={data}
                                        step={step}
                                        setData={setData}
                                        submit={submit}
                                        setStep={setStep}
                                        uiSchema={currentUiSchema}
                                        schema={currentFormSchema}
                                        widgets={widgets}
                                        error={error}
                                        bondRecord={bondRecord}
                                        isSkipBond={isSkipBond}
                                        omitExtraData={
                                          Step.BOND_AGENCY_QUESTIONS
                                            ? true
                                            : undefined
                                        }
                                        liveOmit={
                                          Step.BOND_AGENCY_QUESTIONS
                                            ? true
                                            : undefined
                                        }
                                      />
                                    )}
                                  </>
                                )
                              )}
                            </>
                          )}
                        {step === Step.BOND_INDEMNITOR &&
                          !isLoadingBondIndemnitor && (
                            <FormIndemnitor
                              amountOfIndemnitor={amountOfIndemnitor}
                              isSkipBondIndemnitor={isSkipBondIndemnitor}
                              setFormDataBondIndemnitor={
                                setFormDataBondIndemnitor
                              }
                              setAmountOfIndemnitor={setAmountOfIndemnitor}
                              formDataBondIndemnitor={formDataBondIndemnitor}
                              submit={submit}
                              widgets={widgets}
                              getLefts={getLefts}
                              step={step}
                              schema={currentFormSchema}
                              isLoading={isLoadingBondRecords}
                            />
                          )}

                        {step === Step.BOND_PREVIEW && (
                          <BondPreview
                            paymentLink={
                              typeof paymentLink === "object"
                                ? paymentLink?.href
                                : ""
                            }
                            onSubmit={() => setStep(Step.BOND_DELIVERY)}
                            isLoading={
                              isLoadingBondRecords || isShippingLoading
                            }
                            bondRecord={currentBondRecordData}
                            pdfUrl={pdfUrl}
                            fetchShippingMethods={fetchShippingMethods}
                            fetchShippingOptions={fetchShippingOptions}
                          />
                        )}

                        {step === Step.BOND_DELIVERY && (
                          <BondDelivery
                            bondRecord={currentBondRecordData}
                            isLoading={
                              isLoadingBondRecords || isShippingLoading
                            }
                            shippingMethods={shippingMethods}
                            onSubmit={handleDeliverySubmit}
                            forms={forms}
                            error={error}
                            widgets={widgets}
                            isBusiness={isBusiness}
                            shippingOptions={shippingOptions}
                            fetchShippingOptions={fetchShippingOptions}
                          />
                        )}

                        {(step === Step.PROCESSING ||
                          step === Step.COMPLETE) && (
                          <BondFinal
                            pdfUrl={pdfUrl}
                            bondStatus={
                              issuancesRecordData?.status_name === "BOND_ISSUED"
                                ? issuancesRecordData?.status_name
                                : currentBondRecordData?.status_name
                            }
                            isError={bondRecordRecoveryIsError}
                            isLoading={
                              bondRecordRecoveryIsLoading ||
                              isLoadingBondRecords ||
                              issuancesIsFetching
                            }
                            bondRecord={currentBondRecordData}
                          />
                        )}
                      </div>
                      {!isLoadingBondIndemnitor && activeSchema[step] && (
                        <StyledSubmitInfo>
                          {bondInfo.submitInfo}
                        </StyledSubmitInfo>
                      )}
                    </Wrapper>
                  )}
              </>
            ) : (
              <span>Loading</span>
            )}
          </WrapperForm>
          <Sidebar bondDescription={product?.descr} sidebarData={sidebarData} />
        </ContainerForm>
      </ErrorBoundary>
      {isOpen ? (
        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <StyledContainerModal>
            <StyledImg src={bondPreviewUrl} alt="preview_bond" />
          </StyledContainerModal>
        </Modal>
      ) : null}
    </Container>
  );
};

export default FormBondApp;
